/**
 * Pipeline Report table (Reporting page's fourth tab).
 *
 * One row per `pipeline_report_entry` — no create/delete, only edit. `GET
 * /api/reports/pipeline` returns the whole (securely scoped) result set in one call — see
 * src/api/pipeline-report.ts for why. So sort, filter, and pagination all happen
 * CLIENT-SIDE over that one in-memory array: an inline edit fires exactly one PATCH, and
 * sorting/filtering/paging never touch the network again until the next mutation
 * invalidates the query.
 *
 * Editing is inline, per-cell, click-to-edit (except the Resource Plan Received
 * checkbox, which is always live) — there is no Edit dialog and no Actions column.
 * Every editable cell is optimistic: it flips to the new value the instant the edit
 * commits, and rolls back with an error toast if the PATCH fails (CLAUDE.md's "Optimistic
 * UI for inline edits" rule). It does NOT touch the TanStack Query cache to do this —
 * per this repo's mutation rules, cache truth still only ever moves via
 * `invalidateQueries` — the optimism lives in each cell's own local state, which
 * resyncs to the server value via a `useEffect` the moment the invalidated query
 * refetches.
 */

import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  Search,
  Upload,
  Workflow,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { type BadgeStatus, StatusBadge } from "@/components/ui/status-badge";
import { Textarea } from "@/components/ui/textarea";
import { useConstraintLibrary } from "@/hooks/use-accounts";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type {
  PipelineReportEntryUpdateInput,
  PipelineReportRow,
} from "@/hooks/use-pipeline-report";
import { usePipelineReport, useUpdatePipelineReportEntry } from "@/hooks/use-pipeline-report";
import { useUsers } from "@/hooks/use-users";
import { formatDateMDY } from "@/lib/format";
import { jsonFetch } from "@/lib/json-fetch";
import type { SelectOption } from "@/lib/option-search";
import { formatPlanMoney } from "@/lib/plan-format";
import { cn } from "@/lib/utils";
import { PipelineReportImportDialog } from "./PipelineReportImportDialog";
import { PipelineReportSyncDialog } from "./PipelineReportSyncDialog";
import {
  distinctOptions,
  EMPTY_FILTERS,
  type FilterState,
  filterRows,
} from "./pipeline-report-filters/filter-state";
import { PipelineReportFilterBar } from "./pipeline-report-filters/PipelineReportFilterBar";

const PAGE_SIZE = 50;

const RESOURCING_STATUS_VALUES = ["Staffed", "PartiallyStaffed", "NotStaffed"] as const;
type ResourcingStatus = (typeof RESOURCING_STATUS_VALUES)[number];

function isResourcingStatus(value: string): value is ResourcingStatus {
  return (RESOURCING_STATUS_VALUES as readonly string[]).includes(value);
}

const RESOURCING_STATUS_LABEL: Record<ResourcingStatus, string> = {
  Staffed: "Staffed",
  PartiallyStaffed: "Partially Staffed",
  NotStaffed: "Not Staffed",
};

const RESOURCING_STATUS_BADGE: Record<ResourcingStatus, BadgeStatus> = {
  Staffed: "completed",
  PartiallyStaffed: "pending",
  NotStaffed: "failed",
};

const RESOURCING_STATUS_OPTIONS: SelectOption[] = RESOURCING_STATUS_VALUES.map((value) => ({
  value,
  label: RESOURCING_STATUS_LABEL[value],
}));

const YES_NO_OPTIONS: SelectOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const NONE_OPTION: SelectOption = { value: "", label: "— None —" };

function ResourcingStatusBadge({ status }: { status: ResourcingStatus }) {
  return (
    <StatusBadge status={RESOURCING_STATUS_BADGE[status]} className="h-5 px-1.5">
      {RESOURCING_STATUS_LABEL[status]}
    </StatusBadge>
  );
}

// Ad hoc query — matches the existing call sites (ResourceDetailDrawer, SolutionDetailDrawer)
// so the Resource picker shares their cache under the same key, rather than inventing a
// dedicated hook for a lookup three other components already fetch.
interface ResourceOption {
  id: string;
  name: string;
}

function useResourceOptions() {
  return useQuery({
    queryKey: ["dashboard-resources"],
    queryFn: () => jsonFetch<{ resources: ResourceOption[] }>("/api/dashboard/resources"),
  });
}

/**
 * Ref callback that focuses its element the instant it mounts — the lint-clean stand-in
 * for the `autoFocus` attribute (flagged by lint/a11y/noAutofocus). A plain module-scope
 * function has a stable identity across every render, and React only invokes a ref
 * callback when its identity changes, so this fires exactly once per mount rather than on
 * every re-render — unlike an inline `(el) => el?.focus()`, which gets a new identity
 * every render and would re-steal focus on every keystroke.
 */
function focusOnMount(node: HTMLElement | null): void {
  node?.focus();
}

// ── Sorting ─────────────────────────────────────────────────────────────────────

type SortColumn =
  | "accountName"
  | "deal"
  | "stage"
  | "resource"
  | "resourcingStatus"
  | "resourcePlanReceived"
  | "hoursLogged"
  | "notes"
  | "owner"
  | "platform"
  | "total"
  | "leadSource"
  | "commitDate"
  | "lastActivityAt"
  | "addedAt";

interface SortState {
  column: SortColumn;
  direction: "asc" | "desc";
}

const DEFAULT_SORT: SortState = { column: "accountName", direction: "asc" };

/** Matches the collator config in src/lib/option-search.ts — numeric-aware, case/accent
 * insensitive — so this table alphabetizes consistently with every other picker in the
 * app. Not imported from there because it isn't exported; a second `new Intl.Collator`
 * is cheap. */
const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

// Ascending reads worst-staffed-first — NotStaffed needs attention soonest, Staffed needs
// none. There's no "natural" order for this enum; this is simply the one that reads
// usefully, and it's fixed regardless of which literal value sorts first alphabetically.
const RESOURCING_STATUS_RANK: Record<ResourcingStatus, number> = {
  NotStaffed: 0,
  PartiallyStaffed: 1,
  Staffed: 2,
};

/**
 * Nulls always sort LAST, in both ascending and descending order. This is deliberately
 * NOT the "naive" comparator you'd get from `(a ?? "").localeCompare(b ?? "") * dir`,
 * which flips nulls to the front the instant `dir` is -1 (a descending sort would show
 * blanks first). Null placement is resolved before `dir` is ever applied, so ascending
 * reads real-values-A→Z-then-blanks and descending reads real-values-Z→A-then-blanks —
 * blanks stay at the bottom either way.
 */
function compareNullableString(a: string | null, b: string | null, dir: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return COLLATOR.compare(a, b) * dir;
}

/** Same null-always-last rule as compareNullableString, for the one nullable numeric
 * sort column (Total). */
function compareNullableNumber(a: number | null, b: number | null, dir: 1 | -1): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return (a - b) * dir;
}

function compareRows(a: PipelineReportRow, b: PipelineReportRow, sort: SortState): number {
  const dir: 1 | -1 = sort.direction === "asc" ? 1 : -1;
  switch (sort.column) {
    case "accountName":
      return COLLATOR.compare(a.accountName, b.accountName) * dir;
    case "deal":
      return compareNullableString(a.deal, b.deal, dir);
    case "stage":
      return compareNullableString(a.stage, b.stage, dir);
    case "resource":
      return compareNullableString(a.resourceName, b.resourceName, dir);
    case "resourcingStatus":
      return (
        (RESOURCING_STATUS_RANK[a.resourcingStatus] - RESOURCING_STATUS_RANK[b.resourcingStatus]) *
        dir
      );
    case "resourcePlanReceived":
      return (Number(a.resourcePlanReceived) - Number(b.resourcePlanReceived)) * dir;
    case "hoursLogged":
      return (a.hoursLogged - b.hoursLogged) * dir;
    case "notes":
      return compareNullableString(a.notes, b.notes, dir);
    case "owner":
      return compareNullableString(a.ownerName, b.ownerName, dir);
    case "platform":
      return compareNullableString(a.platformName, b.platformName, dir);
    case "total":
      return compareNullableNumber(
        a.total === null ? null : Number(a.total),
        b.total === null ? null : Number(b.total),
        dir,
      );
    case "leadSource":
      return compareNullableString(a.leadSource, b.leadSource, dir);
    case "commitDate":
      return compareNullableString(a.commitDate, b.commitDate, dir);
    case "lastActivityAt":
      return COLLATOR.compare(a.lastActivityAt, b.lastActivityAt) * dir;
    case "addedAt":
      return COLLATOR.compare(a.addedAt, b.addedAt) * dir;
    default:
      return 0;
  }
}

function sortRows(rows: PipelineReportRow[], sort: SortState): PipelineReportRow[] {
  return [...rows].sort((a, b) => compareRows(a, b, sort));
}

// ── Filtering ───────────────────────────────────────────────────────────────────
//
// FilterState, EMPTY_FILTERS, filterRows, and distinctOptions live in
// ./pipeline-report-filters/filter-state.ts — shared with PipelineReportFilterBar,
// which renders the controls for this state without importing this module.
//
// Global search is a separate, simpler concern: one box, matched against every
// column's displayed value (not just the handful of text-typed columns the Filters
// panel covers) — a coarse "does this row mention X anywhere" check, not a
// column-scoped filter. Kept local to this file rather than filter-state.ts since
// it needs RESOURCING_STATUS_LABEL, which already lives here.

/** True when `term` (case-insensitive, trimmed) appears in ANY column's displayed
 * text for this row — Account, Deal, Stage, Resource, Resourcing Status, Resource
 * Plan Received, Hours Logged, Notes, Owner, Platform, Total, Lead Source, Commit,
 * Last Activity, Added. An empty term matches everything. */
function matchesGlobalSearch(row: PipelineReportRow, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (needle === "") return true;
  const haystack = [
    row.accountName,
    row.deal,
    row.stage,
    row.resourceName,
    RESOURCING_STATUS_LABEL[row.resourcingStatus],
    row.resourcePlanReceived ? "Yes" : "No",
    String(row.hoursLogged),
    row.notes,
    row.ownerName,
    row.platformName,
    row.total,
    row.leadSource,
    row.commitDate,
    row.lastActivityAt,
    row.addedAt,
  ]
    .filter((v): v is string => v !== null && v !== undefined)
    .join("\n")
    .toLowerCase();
  return haystack.includes(needle);
}

// ── Per-cell save plumbing ───────────────────────────────────────────────────────

/** One mutation instance per cell, so `pending` reflects only that cell's own save — not
 * every inline edit on the page. `useUpdatePipelineReportEntry` already toasts on error;
 * `rollback` is this cell's own local-state revert, called in addition. */
function usePipelineFieldSave(rowId: string) {
  const update = useUpdatePipelineReportEntry();
  function save(patch: PipelineReportEntryUpdateInput, rollback: () => void) {
    update.mutate({ id: rowId, patch }, { onError: rollback });
  }
  return { save, pending: update.isPending };
}

type PickListField = "resourceId" | "ownerUserId" | "platformConstraintId";

function buildPickPatch(
  field: PickListField,
  value: string | null,
): PipelineReportEntryUpdateInput {
  switch (field) {
    case "resourceId":
      return { resourceId: value };
    case "ownerUserId":
      return { ownerUserId: value };
    case "platformConstraintId":
      return { platformConstraintId: value };
  }
}

// ── Generic click-to-edit shell ─────────────────────────────────────────────────

/**
 * Owns the display↔edit toggle and the pending/error affordance around a cell's own save.
 * Doesn't know how to save, validate, or format anything — that's the caller's job via
 * `renderEditor`. Display mode is a `<button>` (keyboard-operable: Enter/Space opens it,
 * matching the "keyboard navigation on every primary action" rule).
 */
function InlineEditCell({
  pending,
  display,
  renderEditor,
  align = "left",
  className,
}: {
  pending: boolean;
  display: React.ReactNode;
  renderEditor: (close: () => void) => React.ReactNode;
  align?: "left" | "right";
  className?: string;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return <div className={cn("min-w-0", className)}>{renderEditor(() => setEditing(false))}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      disabled={pending}
      className={cn(
        "flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left transition-colors hover:bg-muted/60 disabled:cursor-wait disabled:opacity-60",
        align === "right" && "flex-row-reverse text-right",
        className,
      )}
    >
      <span className="min-w-0 flex-1 break-words">{display}</span>
      {pending && <Loader2 className="size-3 shrink-0 animate-spin text-muted-foreground" />}
    </button>
  );
}

// ── Pick-list cell (Resource / Owner / Platform) ─────────────────────────────────

function PickListCell({
  rowId,
  field,
  currentId,
  currentLabel,
  options,
  loading,
  placeholder,
}: {
  rowId: string;
  field: PickListField;
  currentId: string | null;
  currentLabel: string | null;
  options: SelectOption[];
  loading: boolean;
  placeholder: string;
}) {
  const [localId, setLocalId] = useState(currentId);
  const [localLabel, setLocalLabel] = useState(currentLabel);
  useEffect(() => {
    setLocalId(currentId);
    setLocalLabel(currentLabel);
  }, [currentId, currentLabel]);

  const { save, pending } = usePipelineFieldSave(rowId);
  const fullOptions = useMemo<SelectOption[]>(() => [NONE_OPTION, ...options], [options]);

  return (
    <InlineEditCell
      pending={pending}
      display={localLabel ?? <span className="text-muted-foreground">—</span>}
      renderEditor={(close) => (
        <SearchableSelect
          options={fullOptions}
          value={localId ?? ""}
          onValueChange={(next) => {
            const nextId = next === "" ? null : next;
            if (nextId === localId) return;
            const nextLabel =
              nextId === null ? null : (options.find((o) => o.value === nextId)?.label ?? null);
            const prevId = localId;
            const prevLabel = localLabel;
            setLocalId(nextId);
            setLocalLabel(nextLabel);
            save(buildPickPatch(field, nextId), () => {
              setLocalId(prevId);
              setLocalLabel(prevLabel);
            });
          }}
          defaultOpen
          loading={loading}
          placeholder={placeholder}
          onOpenChange={(open) => {
            if (!open) close();
          }}
        />
      )}
    />
  );
}

// ── Resourcing Status cell ───────────────────────────────────────────────────────

function ResourcingStatusCell({ rowId, current }: { rowId: string; current: ResourcingStatus }) {
  const [local, setLocal] = useState(current);
  useEffect(() => setLocal(current), [current]);
  const { save, pending } = usePipelineFieldSave(rowId);

  return (
    <InlineEditCell
      pending={pending}
      display={<ResourcingStatusBadge status={local} />}
      renderEditor={(close) => (
        <select
          ref={focusOnMount}
          value={local}
          onChange={(e) => {
            const raw = e.target.value;
            if (!isResourcingStatus(raw) || raw === local) {
              close();
              return;
            }
            const prev = local;
            setLocal(raw);
            close();
            save({ resourcingStatus: raw }, () => setLocal(prev));
          }}
          onBlur={close}
          onKeyDown={(e) => {
            if (e.key === "Escape") close();
          }}
          className="h-8 w-full rounded-md border border-input bg-transparent px-2 text-xs shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
        >
          {RESOURCING_STATUS_VALUES.map((v) => (
            <option key={v} value={v}>
              {RESOURCING_STATUS_LABEL[v]}
            </option>
          ))}
        </select>
      )}
    />
  );
}

// ── Resource Plan Received cell — always a live checkbox, no click-to-edit step ────

function ResourcePlanReceivedCell({ rowId, current }: { rowId: string; current: boolean }) {
  const [local, setLocal] = useState(current);
  useEffect(() => setLocal(current), [current]);
  const { save, pending } = usePipelineFieldSave(rowId);

  return (
    <div className="flex items-center gap-1.5">
      <Checkbox
        checked={local}
        disabled={pending}
        onCheckedChange={(checked) => {
          const next = checked === true;
          if (next === local) return;
          const prev = local;
          setLocal(next);
          save({ resourcePlanReceived: next }, () => setLocal(prev));
        }}
        aria-label="Resource Plan Received"
      />
      {pending && <Loader2 className="size-3 shrink-0 animate-spin text-muted-foreground" />}
    </div>
  );
}

// ── Notes cell — click-to-edit, saves on blur ───────────────────────────────────

function NotesEditor({
  initial,
  onCommit,
  onCancel,
}: {
  initial: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const cancelledRef = useRef(false);
  return (
    <Textarea
      ref={focusOnMount}
      rows={2}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (!cancelledRef.current) onCommit(value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          cancelledRef.current = true;
          onCancel();
        }
      }}
      className="min-h-16 text-sm"
    />
  );
}

function NotesCell({ rowId, current }: { rowId: string; current: string | null }) {
  const [local, setLocal] = useState(current);
  useEffect(() => setLocal(current), [current]);
  const { save, pending } = usePipelineFieldSave(rowId);

  return (
    <InlineEditCell
      pending={pending}
      display={
        local ? (
          <span className="break-words">{local}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      }
      renderEditor={(close) => (
        <NotesEditor
          initial={local ?? ""}
          onCancel={close}
          onCommit={(raw) => {
            const next = raw.trim() === "" ? null : raw;
            if (next === local) {
              close();
              return;
            }
            const prev = local;
            setLocal(next);
            close();
            save({ notes: next }, () => setLocal(prev));
          }}
        />
      )}
    />
  );
}

// ── Sortable column header ───────────────────────────────────────────────────────

function SortableHeader({
  column,
  label,
  align = "left",
  sort,
  onSort,
  className,
}: {
  column: SortColumn;
  label: string;
  align?: "left" | "right";
  sort: SortState;
  onSort: (column: SortColumn) => void;
  /** Extra classes merged onto the <th> — used by the Account/Deal headers to add
   * sticky-left positioning on top of the sticky-top every header already gets. */
  className?: string;
}) {
  const active = sort.column === column;
  return (
    <th
      className={cn(
        // bg-muted, not bg-muted/50 — these headers are sticky (top always, and
        // left on Account/Deal), so a translucent background lets whatever has
        // scrolled underneath show through. Needs to be fully opaque to occlude.
        // text-left is explicit, not incidental: <th> defaults to text-align:center
        // in browsers (unlike <td>), so every header would center itself without it.
        "sticky top-0 z-20 whitespace-nowrap bg-muted px-3 py-2 text-left text-xs font-medium text-muted-foreground",
        align === "right" && "text-right",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          "inline-flex items-center gap-1 hover:text-foreground",
          align === "right" && "flex-row-reverse",
          active && "text-foreground",
        )}
      >
        <span>{label}</span>
        {active ? (
          sort.direction === "asc" ? (
            <ChevronUp className="size-3.5" />
          ) : (
            <ChevronDown className="size-3.5" />
          )
        ) : (
          <ChevronsUpDown className="size-3.5 opacity-30" />
        )}
      </button>
    </th>
  );
}

// ── Skeleton rows ─────────────────────────────────────────────────────────────────

const SKELETON_ROW_IDS = ["sk-r1", "sk-r2", "sk-r3", "sk-r4", "sk-r5"] as const;
const SKELETON_COLUMNS = [
  "account",
  "deal",
  "stage",
  "resource",
  "resourcingStatus",
  "resourcePlanReceived",
  "hoursLogged",
  "notes",
  "owner",
  "platform",
  "total",
  "leadSource",
  "commitDate",
  "lastActivityAt",
  "addedAt",
] as const;

function PipelineReportSkeletonRows() {
  return (
    <>
      {SKELETON_ROW_IDS.map((rowId) => (
        <tr key={rowId} className="border-b last:border-b-0">
          {SKELETON_COLUMNS.map((colKey) => (
            <td key={colKey} className="px-3 py-2.5">
              <Skeleton className="h-4 w-full" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ── Body row ──────────────────────────────────────────────────────────────────────

interface PickListOptionSet {
  resourceOptions: SelectOption[];
  resourcesLoading: boolean;
  ownerOptions: SelectOption[];
  ownersLoading: boolean;
  platformOptions: SelectOption[];
  platformsLoading: boolean;
}

function PipelineReportBodyRow({
  row,
  options,
}: {
  row: PipelineReportRow;
  options: PickListOptionSet;
}) {
  return (
    <tr className="border-b border-border/50 last:border-0 align-top">
      <td
        className="sticky left-0 z-10 w-[140px] break-words bg-background px-3 py-2.5 font-medium"
        title={row.accountName}
      >
        {row.accountName}
      </td>
      <td
        className="sticky left-[140px] z-10 w-[220px] break-words border-r bg-background px-3 py-2.5"
        title={row.deal ?? undefined}
      >
        {row.deal ?? "—"}
      </td>
      <td className="px-3 py-2.5">{row.stage ?? "—"}</td>
      <td className="px-3 py-2.5">
        <PickListCell
          rowId={row.id}
          field="resourceId"
          currentId={row.resourceId}
          currentLabel={row.resourceName}
          options={options.resourceOptions}
          loading={options.resourcesLoading}
          placeholder="Select a Resource…"
        />
      </td>
      <td className="px-3 py-2.5">
        <ResourcingStatusCell rowId={row.id} current={row.resourcingStatus} />
      </td>
      <td className="px-3 py-2.5">
        <ResourcePlanReceivedCell rowId={row.id} current={row.resourcePlanReceived} />
      </td>
      <td className="px-3 py-2.5">{row.hoursLogged}</td>
      <td className="max-w-[220px] px-3 py-2.5">
        <NotesCell rowId={row.id} current={row.notes} />
      </td>
      <td className="px-3 py-2.5">
        <PickListCell
          rowId={row.id}
          field="ownerUserId"
          currentId={row.ownerUserId}
          currentLabel={row.ownerName}
          options={options.ownerOptions}
          loading={options.ownersLoading}
          placeholder="Select an Owner…"
        />
      </td>
      <td className="px-3 py-2.5">
        <PickListCell
          rowId={row.id}
          field="platformConstraintId"
          currentId={row.platformConstraintId}
          currentLabel={row.platformName}
          options={options.platformOptions}
          loading={options.platformsLoading}
          placeholder="Select a Platform…"
        />
      </td>
      <td className="px-3 py-2.5">
        <span className={cn(row.total === null && "text-muted-foreground")}>
          {formatPlanMoney(row.total)}
        </span>
      </td>
      <td className="px-3 py-2.5">{row.leadSource ?? "—"}</td>
      <td className="px-3 py-2.5">
        {row.commitDate ? (
          formatDateMDY(row.commitDate)
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-muted-foreground">{formatDateMDY(row.lastActivityAt)}</td>
      <td className="px-3 py-2.5 text-muted-foreground">{formatDateMDY(row.addedAt)}</td>
    </tr>
  );
}

// ── Table ─────────────────────────────────────────────────────────────────────────

export function PipelineReportTable() {
  const { data, isLoading, isFetching, isError, error, refetch } = usePipelineReport();
  const allRows = useMemo(() => data?.data ?? [], [data]);

  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [importOpen, setImportOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);

  const debouncedSearch = useDebouncedValue(search, 250);
  const debouncedAccount = useDebouncedValue(filters.account, 250);
  const debouncedDeal = useDebouncedValue(filters.deal, 250);
  const debouncedStage = useDebouncedValue(filters.stage, 250);
  const debouncedNotes = useDebouncedValue(filters.notes, 250);
  const debouncedLeadSource = useDebouncedValue(filters.leadSource, 250);

  const effectiveFilters = useMemo<FilterState>(
    () => ({
      ...filters,
      account: debouncedAccount,
      deal: debouncedDeal,
      stage: debouncedStage,
      notes: debouncedNotes,
      leadSource: debouncedLeadSource,
    }),
    [filters, debouncedAccount, debouncedDeal, debouncedStage, debouncedNotes, debouncedLeadSource],
  );

  function updateFilter<K extends keyof FilterState>(key: K, value: FilterState[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  const searched = useMemo(
    () => allRows.filter((r) => matchesGlobalSearch(r, debouncedSearch)),
    [allRows, debouncedSearch],
  );
  const filtered = useMemo(
    () => filterRows(searched, effectiveFilters),
    [searched, effectiveFilters],
  );
  const sorted = useMemo(() => sortRows(filtered, sort), [filtered, sort]);

  // Search, filters, or sort changing invalidates the current page position. This is
  // deliberately NOT a useEffect — the reset doesn't reference debouncedSearch/
  // effectiveFilters/sort inside itself, so an effect would carry dependencies lint
  // correctly flags as unused ("more dependencies than necessary"). Comparing against
  // the previous render's values and adjusting state during render is React's
  // documented alternative (react.dev: "Adjusting some state when a prop changes") and
  // needs no effect at all.
  const resetKeyRef = useRef({ debouncedSearch, effectiveFilters, sort });
  if (
    resetKeyRef.current.debouncedSearch !== debouncedSearch ||
    resetKeyRef.current.effectiveFilters !== effectiveFilters ||
    resetKeyRef.current.sort !== sort
  ) {
    resetKeyRef.current = { debouncedSearch, effectiveFilters, sort };
    if (page !== 1) setPage(1);
  }

  function handleSort(column: SortColumn) {
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" },
    );
  }

  // Option sources for inline edits — the full option-hooks, unchanged from the old
  // dialog, just now feeding table cells instead of dialog fields.
  const resourcesQuery = useResourceOptions();
  const usersQuery = useUsers({});
  const constraintLibraryQuery = useConstraintLibrary();

  const pickListOptions: PickListOptionSet = {
    resourceOptions: (resourcesQuery.data?.resources ?? []).map((r) => ({
      value: r.id,
      label: r.name,
    })),
    resourcesLoading: resourcesQuery.isLoading,
    ownerOptions: (usersQuery.data ?? []).map((u) => ({ value: u.id, label: u.name })),
    ownersLoading: usersQuery.isLoading,
    platformOptions: (constraintLibraryQuery.data ?? [])
      .filter((c) => c.groupName === "Platform" && c.active)
      .map((c) => ({ value: c.id, label: c.name })),
    platformsLoading: constraintLibraryQuery.isLoading,
  };

  // Filter panel dropdown options — distinct values present in the fetched dataset, not a
  // separate API call.
  const resourceFilterOptions = useMemo(
    () =>
      distinctOptions(
        allRows,
        (r) => r.resourceId,
        (r) => r.resourceName,
        COLLATOR,
      ),
    [allRows],
  );
  const ownerFilterOptions = useMemo(
    () =>
      distinctOptions(
        allRows,
        (r) => r.ownerUserId,
        (r) => r.ownerName,
        COLLATOR,
      ),
    [allRows],
  );
  const platformFilterOptions = useMemo(
    () =>
      distinctOptions(
        allRows,
        (r) => r.platformConstraintId,
        (r) => r.platformName,
        COLLATOR,
      ),
    [allRows],
  );

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load Pipeline Report."}
        onRetry={() => refetch()}
      />
    );
  }

  const total = sorted.length;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const clampedPage = Math.min(page, pageCount);
  const startIdx = (clampedPage - 1) * PAGE_SIZE;
  const pageRows = sorted.slice(startIdx, startIdx + PAGE_SIZE);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search all columns…"
              className="h-9 w-64 pl-8 text-sm"
            />
          </div>
          <PipelineReportFilterBar
            filters={filters}
            updateFilter={updateFilter}
            onClear={() => setFilters(EMPTY_FILTERS)}
            options={{
              resourceOptions: resourceFilterOptions,
              ownerOptions: ownerFilterOptions,
              platformOptions: platformFilterOptions,
              resourcingStatusOptions: RESOURCING_STATUS_OPTIONS,
              yesNoOptions: YES_NO_OPTIONS,
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          {/* Import is hidden, not removed — Pipeline Report entries are now synced
              live from the gateway Deal feed (see the Sync button below), which
              supersedes the CSV/XLSX importer for day-to-day use. */}
          {false && (
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="mr-1.5 h-3.5 w-3.5" />
              Import
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setSyncOpen(true)}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Sync
          </Button>
        </div>
      </div>

      {/* max-h + overflow-auto bounds vertical scroll to this container, not the page —
          `sticky top-0` on the header cells needs a scrolling ancestor to stick against. */}
      <div className="max-h-[70vh] overflow-auto rounded-lg border">
        {/* table-fixed is required for the sticky Account/Deal columns: with the
            default auto layout, a column's rendered width is only a hint and can
            grow past its declared w-[…], which desyncs it from the sticky "left"
            offset on the next column and produces visible overlap. Fixed layout
            makes the header row's declared widths authoritative.
            min-w-[2005px] is a floor, not a fixed size — w-full still lets the
            table fill a wide viewport, but on a narrower one the table stops
            shrinking at that floor instead of squeezing the other columns'
            (whitespace-nowrap by default, see SortableHeader) headers into
            wrapping and overlapping their neighbors. Account/Deal are pinned
            (140px + 220px); Platform and the three date columns are pinned
            narrow (90px each); Resourcing Status is pinned wide enough to fit
            its label on one line (135px); Resource Plan Received is pinned
            narrower and allowed to wrap (whitespace-normal override), since
            it's still the widest label in the table; the rest split the
            remainder evenly. The container's horizontal scroll takes over
            below the floor. */}
        <table className="w-full min-w-[2005px] table-fixed text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableHeader
                column="accountName"
                label="Account"
                sort={sort}
                onSort={handleSort}
                className="left-0 z-30 w-[140px]"
              />
              <SortableHeader
                column="deal"
                label="Deal"
                sort={sort}
                onSort={handleSort}
                className="left-[140px] z-30 w-[220px] border-r"
              />
              <SortableHeader column="stage" label="Stage" sort={sort} onSort={handleSort} />
              <SortableHeader column="resource" label="Resource" sort={sort} onSort={handleSort} />
              <SortableHeader
                column="resourcingStatus"
                label="Resourcing Status"
                sort={sort}
                onSort={handleSort}
                className="w-[135px]"
              />
              <SortableHeader
                column="resourcePlanReceived"
                label="Resource Plan Received"
                sort={sort}
                onSort={handleSort}
                className="w-[110px] whitespace-normal"
              />
              <SortableHeader
                column="hoursLogged"
                label="Hours Logged"
                sort={sort}
                onSort={handleSort}
                className="w-[110px]"
              />
              <SortableHeader column="notes" label="Notes" sort={sort} onSort={handleSort} />
              <SortableHeader column="owner" label="Owner" sort={sort} onSort={handleSort} />
              <SortableHeader
                column="platform"
                label="Platform"
                sort={sort}
                onSort={handleSort}
                className="w-[90px]"
              />
              <SortableHeader column="total" label="Total" sort={sort} onSort={handleSort} />
              <SortableHeader
                column="leadSource"
                label="Lead Source"
                sort={sort}
                onSort={handleSort}
              />
              <SortableHeader
                column="commitDate"
                label="Commit"
                sort={sort}
                onSort={handleSort}
                className="w-[90px]"
              />
              <SortableHeader
                column="lastActivityAt"
                label="Last Activity"
                sort={sort}
                onSort={handleSort}
                className="w-[90px]"
              />
              <SortableHeader
                column="addedAt"
                label="Added"
                sort={sort}
                onSort={handleSort}
                className="w-[90px]"
              />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <PipelineReportSkeletonRows />
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={SKELETON_COLUMNS.length}>
                  <EmptyState
                    icon={Workflow}
                    title="No Pipeline Report entries"
                    description={
                      allRows.length === 0
                        ? "No Pipeline Report entries yet."
                        : "No entries match the current filters."
                    }
                  />
                </td>
              </tr>
            ) : (
              pageRows.map((row) => (
                <PipelineReportBodyRow key={row.id} row={row} options={pickListOptions} />
              ))
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && !isError && total > 0 && (
        <CursorPagination
          total={total}
          page={clampedPage}
          limit={PAGE_SIZE}
          hasNext={clampedPage < pageCount}
          hasPrev={clampedPage > 1}
          onNext={() => setPage((p) => p + 1)}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          loading={isFetching}
          itemLabel="entries"
        />
      )}

      <PipelineReportImportDialog open={importOpen} onOpenChange={setImportOpen} />
      <PipelineReportSyncDialog open={syncOpen} onOpenChange={setSyncOpen} />
    </div>
  );
}
